import { useAppContext } from "@context/app";
import { useUI } from "@context/ui-managed-context";
import { IProductDetailModel, ISkuModel } from "@types";
import { addToCart } from "@utils/addToCart";
import { getNotifyConfig } from "@utils/helpers";
import { segmentEvent } from "@utils/segment";
import { productApi } from "gate";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { useState } from "react";
import { toast } from "react-toastify";

function useProductDetail() {
  const { user, country, setUserCart, currency_iso, area, cart, freeShipping } = useAppContext();
  const { locale } = useRouter();
  const t = useTranslations();
  const [addToCartState, setAddToCart] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [count] = useState<number>(1);
  const { setModalView, openModal } = useUI();
  const [_data, setData] = useState<IProductDetailModel | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSku, setSelectedSku] = useState<ISkuModel | null>(null);

  const ItemsQty = cart.reduce((previousValue, currentValue) => {
    return previousValue + currentValue.qty;
  }, 0);
  const totalPrice = cart.reduce((previousValue, currentValue) => {
    return previousValue + currentValue.qty * (currentValue.raw_price || currentValue.price);
  }, 0);
  const showBuyLimitMsg = () => {
    toast.error(
      t("client_errors_onlyremaining").replace("@", `${selectedSku?.buy_limit}`),
      getNotifyConfig()
    );
  };

  const handleAddToCart = () => {
    if (_data && selectedSku) {
      if(_data.is_digital){
        return window.location.replace(`/?send_gift=${selectedSku.id}`)
      }
      setAddToCart(true);
      segmentEvent("SKU Added", {
        product_id: _data.id,
        product_slug: _data.slug,
        sku_id: selectedSku?.id,
        sku_slug: selectedSku?.slug,
        brand_id: _data.brand.id,
        brand_slug: _data.brand.slug,
        category_id: _data.category.id,
        category_slug: _data.category.slug,
        subcategory_id: _data.subcategory.id,
        subcategory_slug: _data.subcategory.slug,
        sku_old_price: selectedSku.price.old_price || selectedSku.price.price,
        sku_new_price: selectedSku.price.price,
        price: selectedSku.price.price,
        currency: currency_iso.toUpperCase(),
        country_id: country?.id,
        country_slug: country?.slug,
        area_id: area?.id,
        area_slug: area?.slug,
        user_id: user.data?.id || -1,
        language: locale?.includes("en-") ? "English" : "Arabic",
        from_page: "product_details",
        from_entity: "product",
        from_entity_slug: selectedSku.slug,
        from_page_section: "",
        from_page_section_entity: "",
        from_page_section_slug: "",
        cart_count: ItemsQty + 1,
        quantity: ItemsQty + 1,
        subtotal_amount: totalPrice + (selectedSku.price?.raw_price || selectedSku.price.price),
        free_shipping_threshold: freeShipping ?? undefined,
      });

      setTimeout(() => {
        setAddToCart(false);
      }, 1000);

      setUserCart(
        addToCart(
          cart,
          { ..._data, default_sku_id: selectedSku.id },
          () => {
            setShowSuccess(true);
          },
          count
        )
      );
    }
  };

  const handleNotifySku = async (skuId: number) => {
    if (!user.isLoggedIn) {
      setModalView("LOGIN_VIEW");
      openModal();
      return;
    }

    setIsLoading(true);

    try {
      await productApi.notify(skuId).fetch();
      setData((pre) => {
        if (pre) {
          pre.skus = pre.skus.map((iterator) => {
            if (iterator.id === skuId) iterator.will_notify = true;
            return iterator;
          });
          return pre;
        }
      });

      setSelectedSku((pre) => {
        if (pre) {
          pre.will_notify = true;
          return pre;
        } else {
          return pre;
        }
      });

      handleNotifyEvent("Add");
    } catch (err: any) {
      if (err?.error?.code === 401) {
        setModalView("LOGIN_VIEW");
        openModal();
      }
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelNotifySku = async (skuId: number) => {
    if (!area) return;
    setIsLoading(true);
    try {
      await productApi.cancelNotify(skuId, area.id).fetch();
      setData((pre) => {
        if (pre) {
          pre.skus = pre.skus.map((iterator) => {
            if (iterator.id === skuId) iterator.will_notify = false;
            return iterator;
          });
          return pre;
        }
      });

      setSelectedSku((pre) => {
        if (pre) {
          pre.will_notify = false;
          return pre;
        } else {
          return pre;
        }
      });

      handleNotifyEvent("Remove");
    } catch (err: any) {
      if (err?.error?.code === 401) {
        setModalView("LOGIN_VIEW");
        openModal();
      }
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotifyEvent = (action: "Add" | "Remove") => {
    if (_data) {
      segmentEvent("Availability Alert Updated", {
        product_id: _data.id,
        product_slug: _data.slug,
        sku_id: selectedSku?.id,
        sku_slug: selectedSku?.slug,
        sku_old_price: selectedSku?.price.old_price || selectedSku?.price.price,
        sku_new_price: selectedSku?.price.price,
        price: selectedSku?.price.price,
        country_id: country?.id,
        country_slug: country?.slug,
        area_id: area?.id,
        area_slug: area?.slug,
        user_id: user.data?.id || -1,
        language: locale?.includes("en") ? "English" : "Arabic",
        out_of_stock: selectedSku?.buy_limit ? false : true,
        from_page: "product_details",
        from_entity: "product",
        from_entity_slug: selectedSku?.slug,
        from_page_section: "",
        from_page_section_entity: "",
        from_page_section_slug: "",
        action,
      });
    }
  };

  return {
    showBuyLimitMsg,
    addToCartState,
    setShowSuccess,
    showSuccess,
    handleAddToCart,
    handleNotifySku,
    handleCancelNotifySku,
    isLoading,
    setIsLoading,
    _data,
    setData,
    selectedSku,
    setSelectedSku,
  };
}

export default useProductDetail;
